import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import {
  ColorPickerFieldComponent,
  DataFormActionsComponent,
  DataFormCardComponent,
  DataFormGridComponent,
  ErrorBlockComponent,
} from '@clinic-platform/ui-components';
import { AuthService } from '../../../core/auth/auth.service';
import { TenantBrandingService } from '../../../core/branding/tenant-branding.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CompaniesService } from '../../companies/data-access/companies.service';
import { CompanyBranding } from '../../companies/data-access/companies.models';
import { BrandingAssetFieldComponent } from './branding-asset-field.component';
import { SETTINGS_TAB_FORM_STYLES } from './settings-tab-form.styles';
import { CompanyScopedSettingsTabBase } from './settings-tab-state.base';

type BrandingAssetKind = 'logo' | 'sidebar' | 'content' | 'topbar';

interface BrandingAssetState {
  file: File | null;
  selectedName: string;
  url: string | null;
  isUploading: boolean;
  isRemoving: boolean;
  uploadLabel: string;
  removeLabel: string;
  uploadSuccess: string;
  removeSuccess: string;
  upload: (file: File, companyId?: string) => Observable<CompanyBranding>;
  remove: (companyId?: string) => Observable<CompanyBranding>;
}

@Component({
  selector: 'app-branding-settings-tab',
  standalone: true,
  imports: [
    BrandingAssetFieldComponent,
    ColorPickerFieldComponent,
    DataFormActionsComponent,
    DataFormCardComponent,
    DataFormGridComponent,
    ErrorBlockComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-data-form-card>
      <h3>Branding</h3>
      <p class="settings-tab__hint">
        {{ contextText }}
      </p>

      <form [formGroup]="form" (ngSubmit)="save()">
        <lib-data-form-grid>
          <label>
            Display name
            <input type="text" formControlName="displayName" maxlength="120" />
          </label>

          <label>
            Logo
            <app-branding-asset-field
              hint="Recommended: 320x96 px, wide logo, transparent background when possible. Best fit: about 10:3 ratio."
              [selectedName]="selectedAssetName('logo')"
              [assetUrl]="assetUrl('logo')"
              [hasSelected]="hasSelectedAsset('logo')"
              [disabled]="!canSaveSettings || isAssetBusy"
              [isUploading]="isUploadingAsset('logo')"
              [isRemoving]="isRemovingAsset('logo')"
              [uploadLabel]="assetUploadLabel('logo')"
              [removeLabel]="assetRemoveLabel('logo')"
              (selected)="selectAsset('logo', $event)"
              (upload)="uploadAsset('logo')"
              (remove)="removeAsset('logo')"
            />
          </label>

          <label>
            Primary color
            <lib-color-picker-field
              formControlName="primaryColor"
              placeholder="#2563eb"
              fallbackColor="#2563eb"
              ariaLabel="Primary color hex"
              pickerAriaLabel="Pick primary color"
            />
          </label>

          <label>
            Secondary color
            <lib-color-picker-field
              formControlName="secondaryColor"
              placeholder="#0f172a"
              fallbackColor="#0f172a"
              ariaLabel="Secondary color hex"
              pickerAriaLabel="Pick secondary color"
            />
          </label>

          <label>
            Accent color
            <lib-color-picker-field
              formControlName="accentColor"
              placeholder="#f59e0b"
              fallbackColor="#f59e0b"
              ariaLabel="Accent color hex"
              pickerAriaLabel="Pick accent color"
            />
          </label>

          <label>
            Available slot color
            <lib-color-picker-field
              formControlName="slotAvailableColor"
              placeholder="#22c55e"
              fallbackColor="#22c55e"
              ariaLabel="Available slot color hex"
              pickerAriaLabel="Pick available slot color"
            />
          </label>

          <label>
            Unavailable slot color
            <lib-color-picker-field
              formControlName="slotUnavailableColor"
              placeholder="#f3f4f6"
              fallbackColor="#f3f4f6"
              ariaLabel="Unavailable slot color hex"
              pickerAriaLabel="Pick unavailable slot color"
            />
          </label>

          <label>
            Slot pressure color
            <lib-color-picker-field
              formControlName="slotPressureColor"
              placeholder="#f59e0b"
              fallbackColor="#f59e0b"
              ariaLabel="Slot pressure color hex"
              pickerAriaLabel="Pick slot pressure color"
            />
          </label>

          <label>
            Past slot pattern color
            <lib-color-picker-field
              formControlName="slotPastPatternColor"
              placeholder="#64748b"
              fallbackColor="#64748b"
              ariaLabel="Past slot pattern color hex"
              pickerAriaLabel="Pick past slot pattern color"
            />
          </label>

          <label>
            Past slot pattern opacity (%)
            <input type="number" formControlName="slotPastPatternOpacity" min="0" max="100" />
          </label>

          <label>
            Menu bar background color
            <lib-color-picker-field
              formControlName="sidebarBackgroundColor"
              placeholder="#0f172a"
              fallbackColor="#0f172a"
              ariaLabel="Menu bar background color hex"
              pickerAriaLabel="Pick menu bar background color"
            />
          </label>

          <label>
            Menu bar text color
            <lib-color-picker-field
              formControlName="sidebarTextColor"
              placeholder="#ffffff"
              fallbackColor="#ffffff"
              ariaLabel="Menu bar text color hex"
              pickerAriaLabel="Pick menu bar text color"
            />
          </label>

          <label>
            Menu background overlay opacity (%)
            <input type="number" formControlName="sidebarBackgroundOverlayOpacity" min="0" max="100" />
          </label>

          <label>
            Main background overlay opacity (%)
            <input type="number" formControlName="contentBackgroundOverlayOpacity" min="0" max="100" />
          </label>

          <label>
            Header background overlay opacity (%)
            <input type="number" formControlName="topbarBackgroundOverlayOpacity" min="0" max="100" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="showCompanyNameInHeader" />
            Show company name in header
          </label>

          <label class="settings-tab__wide">
            Menu bar background image
            <app-branding-asset-field
              hint="Recommended: 1200x1600 px. Portrait-friendly image with a calm center area. Best fit: about 3:4 ratio."
              [selectedName]="selectedAssetName('sidebar')"
              [assetUrl]="assetUrl('sidebar')"
              [hasSelected]="hasSelectedAsset('sidebar')"
              [disabled]="!canSaveSettings || isAssetBusy"
              [isUploading]="isUploadingAsset('sidebar')"
              [isRemoving]="isRemovingAsset('sidebar')"
              [uploadLabel]="assetUploadLabel('sidebar')"
              [removeLabel]="assetRemoveLabel('sidebar')"
              (selected)="selectAsset('sidebar', $event)"
              (upload)="uploadAsset('sidebar')"
              (remove)="removeAsset('sidebar')"
            />
          </label>

          <label class="settings-tab__wide">
            Main area background image
            <app-branding-asset-field
              hint="Recommended: 1600x900 px. Landscape image with safe empty space for content. Best fit: 16:9 ratio."
              [selectedName]="selectedAssetName('content')"
              [assetUrl]="assetUrl('content')"
              [hasSelected]="hasSelectedAsset('content')"
              [disabled]="!canSaveSettings || isAssetBusy"
              [isUploading]="isUploadingAsset('content')"
              [isRemoving]="isRemovingAsset('content')"
              [uploadLabel]="assetUploadLabel('content')"
              [removeLabel]="assetRemoveLabel('content')"
              (selected)="selectAsset('content', $event)"
              (upload)="uploadAsset('content')"
              (remove)="removeAsset('content')"
            />
          </label>

          <label class="settings-tab__wide">
            Header background image
            <app-branding-asset-field
              hint="Recommended: 1600x320 px. Panoramic header strip with low-detail center area. Best fit: about 5:1 ratio."
              [selectedName]="selectedAssetName('topbar')"
              [assetUrl]="assetUrl('topbar')"
              [hasSelected]="hasSelectedAsset('topbar')"
              [disabled]="!canSaveSettings || isAssetBusy"
              [isUploading]="isUploadingAsset('topbar')"
              [isRemoving]="isRemovingAsset('topbar')"
              [uploadLabel]="assetUploadLabel('topbar')"
              [removeLabel]="assetRemoveLabel('topbar')"
              (selected)="selectAsset('topbar', $event)"
              (upload)="uploadAsset('topbar')"
              (remove)="removeAsset('topbar')"
            />
          </label>
        </lib-data-form-grid>

        <lib-data-form-actions>
          <button type="submit" [disabled]="form.invalid || !canSaveSettings || isAssetBusy">
            {{ isSaving ? 'Saving...' : 'Save branding' }}
          </button>
        </lib-data-form-actions>
      </form>

      @if (previewDisplayName || assetUrl('logo')) {
        <div class="settings-page__branding-preview">
          @if (assetUrl('logo')) {
            <img
              [src]="assetUrl('logo')"
              alt="Branding preview logo"
              class="settings-page__branding-logo"
            />
          }
          <div class="settings-page__branding-swatch-row">
            <span class="settings-page__branding-swatch" [style.background]="colorValue('primaryColor', '#2563eb')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('secondaryColor', '#0f172a')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('accentColor', '#f59e0b')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('slotAvailableColor', '#22c55e')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('slotUnavailableColor', '#f3f4f6')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('slotPressureColor', '#f59e0b')"></span>
            <span
              class="settings-page__branding-swatch"
              [style.background]="colorValue('slotPastPatternColor', '#64748b')"
              [style.opacity]="opacityValue('slotPastPatternOpacity') / 100"
            ></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('sidebarBackgroundColor', '#0f172a')"></span>
            <span class="settings-page__branding-swatch" [style.background]="colorValue('sidebarTextColor', '#ffffff')"></span>
          </div>
          <div class="settings-page__branding-frames">
            <div class="settings-page__branding-frame settings-page__branding-frame--sidebar">
              <div
                class="settings-page__branding-frame-media settings-page__branding-frame-media--sidebar"
                [style.background-color]="colorValue('sidebarBackgroundColor', '#0f172a')"
                [style.background-image]="assetUrl('sidebar') ? 'linear-gradient(rgba(15, 23, 42, ' + opacityValue('sidebarBackgroundOverlayOpacity') / 100 + '), rgba(15, 23, 42, ' + opacityValue('sidebarBackgroundOverlayOpacity') / 100 + ')), url(' + assetUrl('sidebar') + ')' : 'none'"
              >
                <span [style.color]="colorValue('sidebarTextColor', '#ffffff')">Menu bar</span>
              </div>
              <div class="settings-page__frame-caption">Portrait-safe cover</div>
            </div>
            <div class="settings-page__branding-frame settings-page__branding-frame--topbar">
              <div
                class="settings-page__branding-frame-media settings-page__branding-frame-media--topbar"
                [style.background-image]="assetUrl('topbar') ? 'linear-gradient(rgba(255, 255, 255, ' + opacityValue('topbarBackgroundOverlayOpacity') / 100 + '), rgba(255, 255, 255, ' + opacityValue('topbarBackgroundOverlayOpacity') / 100 + ')), url(' + assetUrl('topbar') + ')' : 'none'"
              >
                <span>Header</span>
              </div>
              <div class="settings-page__frame-caption">Panoramic crop</div>
            </div>
            <div class="settings-page__branding-frame settings-page__branding-frame--content">
              <div
                class="settings-page__branding-frame-media settings-page__branding-frame-media--content"
                [style.background-image]="assetUrl('content') ? 'linear-gradient(rgba(248, 250, 252, ' + opacityValue('contentBackgroundOverlayOpacity') / 100 + '), rgba(248, 250, 252, ' + opacityValue('contentBackgroundOverlayOpacity') / 100 + ')), url(' + assetUrl('content') + ')' : 'none'"
              >
                <span>Content area</span>
              </div>
              <div class="settings-page__frame-caption">Landscape-safe cover</div>
            </div>
          </div>
          <div class="settings-tab__meta">Preview name: {{ previewDisplayName }}</div>
          @if (assetUrl('sidebar')) {
            <div class="settings-tab__meta">Menu bar background image is configured</div>
          }
          @if (assetUrl('content')) {
            <div class="settings-tab__meta">Main area background image is configured</div>
          }
          @if (assetUrl('topbar')) {
            <div class="settings-tab__meta">Header background image is configured</div>
          }
        </div>
      }

      @if (errorText) {
        <lib-error-block variant="error" [message]="(errorText | t) ?? ''" />
      }

      @if (successText) {
        <lib-error-block variant="success" [message]="successText" />
      }
    </lib-data-form-card>
  `,
  styles: [SETTINGS_TAB_FORM_STYLES, `
    .settings-page__branding-preview {
      margin-top: 16px;
      display: grid;
      gap: 10px;
      justify-items: start;
      padding: 14px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #f8fafc;
    }

    .settings-page__branding-logo {
      max-width: 180px;
      max-height: 56px;
      object-fit: contain;
      display: block;
    }

    .settings-page__branding-swatch-row {
      display: flex;
      gap: 10px;
    }

    .settings-page__branding-swatch {
      width: 28px;
      height: 28px;
      border-radius: 999px;
      border: 1px solid #cbd5e1;
      display: inline-block;
    }

    .settings-page__branding-frames {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
    }

    .settings-page__branding-frame {
      display: grid;
      gap: 8px;
    }

    .settings-page__branding-frame-media {
      display: flex;
      align-items: flex-start;
      border: 1px solid #dbe2ea;
      border-radius: 14px;
      overflow: hidden;
      background-color: #e5e7eb;
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
    }

    .settings-page__branding-frame-media span {
      display: inline-flex;
      margin: 10px;
      padding: 4px 8px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.78);
      color: #111827;
      font-size: 12px;
      font-weight: 700;
    }

    .settings-page__branding-frame-media--sidebar {
      min-height: 180px;
      align-items: flex-start;
      color: #ffffff;
    }

    .settings-page__branding-frame-media--content {
      min-height: 140px;
    }

    .settings-page__branding-frame-media--topbar {
      min-height: 72px;
    }

    .settings-page__frame-caption {
      color: #6b7280;
      font-size: 13px;
    }

    @media (max-width: 640px) {
      .settings-page__branding-preview {
        padding: 12px;
      }

      .settings-page__branding-frames {
        grid-template-columns: 1fr;
      }

      .settings-page__branding-frame-media--sidebar {
        min-height: 140px;
      }

      .settings-page__branding-frame-media--content {
        min-height: 120px;
      }
    }
  `],
})
export class BrandingSettingsTabComponent extends CompanyScopedSettingsTabBase implements OnInit
{
  @Input() public contextText = '';

  private readonly companiesService = inject(CompaniesService);
  private readonly tenantBranding = inject(TenantBrandingService);
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly form = this.formBuilder.group({
    displayName: this.formBuilder.nonNullable.control('', [Validators.maxLength(120)]),
    primaryColor: this.formBuilder.nonNullable.control('#2563eb', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    secondaryColor: this.formBuilder.nonNullable.control('#0f172a', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    accentColor: this.formBuilder.nonNullable.control('#f59e0b', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    slotAvailableColor: this.formBuilder.nonNullable.control('#22c55e', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    slotUnavailableColor: this.formBuilder.nonNullable.control('#f3f4f6', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    slotPressureColor: this.formBuilder.nonNullable.control('#f59e0b', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    slotPastPatternColor: this.formBuilder.nonNullable.control('#64748b', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    slotPastPatternOpacity: this.formBuilder.nonNullable.control(20, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    sidebarBackgroundColor: this.formBuilder.nonNullable.control('#0f172a', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    sidebarTextColor: this.formBuilder.nonNullable.control('#ffffff', [Validators.pattern(/^#([0-9a-fA-F]{6})$/)]),
    sidebarBackgroundOverlayOpacity: this.formBuilder.nonNullable.control(88, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    contentBackgroundOverlayOpacity: this.formBuilder.nonNullable.control(93, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    topbarBackgroundOverlayOpacity: this.formBuilder.nonNullable.control(82, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    showCompanyNameInHeader: this.formBuilder.nonNullable.control(true),
  });

  private readonly assets: Record<BrandingAssetKind, BrandingAssetState> = {
    logo: {
      file: null,
      selectedName: '',
      url: null,
      isUploading: false,
      isRemoving: false,
      uploadLabel: 'Upload logo',
      removeLabel: 'Remove logo',
      uploadSuccess: 'Branding logo uploaded',
      removeSuccess: 'Branding logo removed',
      upload: (file, companyId) => this.companiesService.uploadBrandingLogo(file, companyId),
      remove: (companyId) => this.companiesService.removeBrandingLogo(companyId),
    },
    sidebar: {
      file: null,
      selectedName: '',
      url: null,
      isUploading: false,
      isRemoving: false,
      uploadLabel: 'Upload menu background',
      removeLabel: 'Remove menu background',
      uploadSuccess: 'Menu background uploaded',
      removeSuccess: 'Menu background removed',
      upload: (file, companyId) => this.companiesService.uploadSidebarBackground(file, companyId),
      remove: (companyId) => this.companiesService.removeSidebarBackground(companyId),
    },
    content: {
      file: null,
      selectedName: '',
      url: null,
      isUploading: false,
      isRemoving: false,
      uploadLabel: 'Upload main background',
      removeLabel: 'Remove main background',
      uploadSuccess: 'Main background uploaded',
      removeSuccess: 'Main background removed',
      upload: (file, companyId) => this.companiesService.uploadContentBackground(file, companyId),
      remove: (companyId) => this.companiesService.removeContentBackground(companyId),
    },
    topbar: {
      file: null,
      selectedName: '',
      url: null,
      isUploading: false,
      isRemoving: false,
      uploadLabel: 'Upload header background',
      removeLabel: 'Remove header background',
      uploadSuccess: 'Header background uploaded',
      removeSuccess: 'Header background removed',
      upload: (file, companyId) => this.companiesService.uploadTopbarBackground(file, companyId),
      remove: (companyId) => this.companiesService.removeTopbarBackground(companyId),
    },
  };

  public ngOnInit(): void
  {
    this.reloadSettings();
  }

  protected get previewDisplayName(): string
  {
    const displayName = this.form.controls.displayName.value.trim();
    if (displayName) {
      return displayName;
    }

    return this.authService.getPreferredCompanySlug() || 'Tenant Preview';
  }

  protected get isAssetBusy(): boolean
  {
    return (Object.keys(this.assets) as BrandingAssetKind[]).some((kind) =>
      this.assets[kind].isUploading || this.assets[kind].isRemoving,
    );
  }

  protected save(): void
  {
    if (this.form.invalid || !this.canSaveSettings || this.isAssetBusy) {
      return;
    }

    this.beginSaving();

    const value = this.form.getRawValue();

    this.currentSettingsRequest(this.companiesService.updateCurrentBranding({
      displayName: value.displayName.trim() || undefined,
      primaryColor: value.primaryColor.trim() || undefined,
      secondaryColor: value.secondaryColor.trim() || undefined,
      accentColor: value.accentColor.trim() || undefined,
      slotAvailableColor: value.slotAvailableColor.trim() || undefined,
      slotUnavailableColor: value.slotUnavailableColor.trim() || undefined,
      slotPressureColor: value.slotPressureColor.trim() || undefined,
      slotPastPatternColor: value.slotPastPatternColor.trim() || undefined,
      slotPastPatternOpacity: value.slotPastPatternOpacity,
      sidebarBackgroundColor: value.sidebarBackgroundColor.trim() || undefined,
      sidebarTextColor: value.sidebarTextColor.trim() || undefined,
      sidebarBackgroundOverlayOpacity: value.sidebarBackgroundOverlayOpacity,
      contentBackgroundOverlayOpacity: value.contentBackgroundOverlayOpacity,
      topbarBackgroundOverlayOpacity: value.topbarBackgroundOverlayOpacity,
      showCompanyNameInHeader: value.showCompanyNameInHeader,
    }, this.companyId)).subscribe({
      next: (branding) => {
        this.applyBrandingState(branding);
        this.setSuccess('Branding saved');
        this.finishSaving();
      },
      error: (error: unknown) => {
        this.setHttpError(error);
        this.finishSaving();
      },
    });
  }

  protected selectAsset(kind: BrandingAssetKind, event: Event): void
  {
    if (!this.canSaveSettings || this.isAssetBusy) return;
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;
    this.assets[kind].file = file;
    this.assets[kind].selectedName = file?.name ?? '';
    if (target) {
      target.value = '';
    }
  }

  protected uploadAsset(kind: BrandingAssetKind): void
  {
    const asset = this.assets[kind];
    if (!asset.file || !this.canSaveSettings || this.isAssetBusy) {
      return;
    }

    asset.isUploading = true;
    this.clearFeedback();

    this.currentSettingsRequest(asset.upload(asset.file, this.companyId)).subscribe({
      next: (branding) => {
        this.applyBrandingState(branding);
        this.clearSelectedAsset(kind);
        asset.isUploading = false;
        this.setSuccess(asset.uploadSuccess);
      },
      error: (error: unknown) => {
        asset.isUploading = false;
        this.setHttpError(error);
      },
    });
  }

  protected removeAsset(kind: BrandingAssetKind): void
  {
    const asset = this.assets[kind];
    if (!asset.url || !this.canSaveSettings || this.isAssetBusy) {
      return;
    }

    asset.isRemoving = true;
    this.clearFeedback();

    this.currentSettingsRequest(asset.remove(this.companyId)).subscribe({
      next: (branding) => {
        this.applyBrandingState(branding);
        this.clearSelectedAsset(kind);
        asset.isRemoving = false;
        this.setSuccess(asset.removeSuccess);
      },
      error: (error: unknown) => {
        asset.isRemoving = false;
        this.setHttpError(error);
      },
    });
  }

  protected selectedAssetName(kind: BrandingAssetKind): string
  {
    return this.assets[kind].selectedName;
  }

  protected hasSelectedAsset(kind: BrandingAssetKind): boolean
  {
    return !!this.assets[kind].file;
  }

  protected assetUrl(kind: BrandingAssetKind): string | null
  {
    return this.assets[kind].url;
  }

  protected isUploadingAsset(kind: BrandingAssetKind): boolean
  {
    return this.assets[kind].isUploading;
  }

  protected isRemovingAsset(kind: BrandingAssetKind): boolean
  {
    return this.assets[kind].isRemoving;
  }

  protected assetUploadLabel(kind: BrandingAssetKind): string
  {
    return this.assets[kind].uploadLabel;
  }

  protected assetRemoveLabel(kind: BrandingAssetKind): string
  {
    return this.assets[kind].removeLabel;
  }

  protected colorValue(controlName: string, fallback: string): string
  {
    const value = this.form.get(controlName)?.value;
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  protected opacityValue(controlName: string): number
  {
    const value = Number(this.form.get(controlName)?.value);
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(100, value));
  }

  protected reloadSettings(): void
  {
    for (const asset of Object.values(this.assets)) {
      asset.file = null;
      asset.selectedName = '';
      asset.url = null;
      asset.isUploading = false;
      asset.isRemoving = false;
    }

    this.loadSettings(this.companiesService.getCurrentBranding(this.companyId), this.form).subscribe({
      next: (branding) => {
        this.applyBrandingState(branding);
      },
      error: (error: unknown) => {
        this.setHttpError(error);
      },
    });
  }

  private clearSelectedAsset(kind: BrandingAssetKind): void
  {
    this.assets[kind].file = null;
    this.assets[kind].selectedName = '';
  }

  private applyBrandingState(branding: CompanyBranding): void
  {
    this.form.patchValue({
      displayName: branding.displayName,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      accentColor: branding.accentColor,
      slotAvailableColor: branding.slotAvailableColor,
      slotUnavailableColor: branding.slotUnavailableColor,
      slotPressureColor: branding.slotPressureColor,
      slotPastPatternColor: branding.slotPastPatternColor,
      slotPastPatternOpacity: branding.slotPastPatternOpacity,
      sidebarBackgroundColor: branding.sidebarBackgroundColor,
      sidebarTextColor: branding.sidebarTextColor,
      sidebarBackgroundOverlayOpacity: branding.sidebarBackgroundOverlayOpacity,
      contentBackgroundOverlayOpacity: branding.contentBackgroundOverlayOpacity,
      topbarBackgroundOverlayOpacity: branding.topbarBackgroundOverlayOpacity,
      showCompanyNameInHeader: branding.showCompanyNameInHeader,
    });

    this.assets.logo.url = branding.logoUrl ?? null;
    this.assets.sidebar.url = branding.sidebarBackgroundImageUrl ?? null;
    this.assets.content.url = branding.contentBackgroundImageUrl ?? null;
    this.assets.topbar.url = branding.topbarBackgroundImageUrl ?? null;

    this.tenantBranding.primeBranding({
      companyId: branding.companyId,
      slug: branding.slug,
      displayName: branding.displayName,
      legalName: branding.legalName,
      logoUrl: branding.logoUrl,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      accentColor: branding.accentColor,
      slotAvailableColor: branding.slotAvailableColor,
      slotUnavailableColor: branding.slotUnavailableColor,
      slotPressureColor: branding.slotPressureColor,
      slotPastPatternColor: branding.slotPastPatternColor,
      slotPastPatternOpacity: branding.slotPastPatternOpacity,
      sidebarBackgroundColor: branding.sidebarBackgroundColor,
      sidebarTextColor: branding.sidebarTextColor,
      sidebarBackgroundOverlayOpacity: branding.sidebarBackgroundOverlayOpacity,
      sidebarBackgroundImageUrl: branding.sidebarBackgroundImageUrl,
      contentBackgroundOverlayOpacity: branding.contentBackgroundOverlayOpacity,
      contentBackgroundImageUrl: branding.contentBackgroundImageUrl,
      topbarBackgroundOverlayOpacity: branding.topbarBackgroundOverlayOpacity,
      topbarBackgroundImageUrl: branding.topbarBackgroundImageUrl,
      showCompanyNameInHeader: branding.showCompanyNameInHeader,
    });
  }
}
